import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { asset } from "./assets";
import { countriesOf, FLAGSHIP_ID, getTrip, HOSTELS, nightsOf, type Trip } from "./content";
import { routePreviewHref } from "./links";
import LiveMap from "./LiveMap";
import { hostelMarker, hostelPoint, poiMarkers } from "./mapModel";
import { Arrow, Flag } from "./ui";
import styles from "./MadMonkeyHome.module.scss";
import shared from "./MadMonkey.module.scss";

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ");

/** The four stops the hero's tour dives into, in the flagship route's order */
const TOUR_STOPS = ["bangkok", "luang-prabang", "hoi-an", "koh-rong"];
const DIVE_ZOOM = 12.2;
const SLIDES = ["koh-sdach-0", "nacpan-beach-0", "luang-prabang-2", "gili-trawangan-1", "koh-rong-0", "vang-vieng-2"];

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The hero card's map: the flagship route on one live map, then a tour that dives into one stop at a time (its
 * hostel with the photo card, the places around it) and flies back out. Still when the visitor prefers reduced
 * motion; paused while the pointer is over the card.
 */
const HeroMap = ({ trip, paused }: { trip: Trip; paused: boolean }) => {
	const [stop, setStop] = useState<string | null>(null);
	const [captionOn, setCaptionOn] = useState(false);
	const [step, setStep] = useState(0);
	const pausedRef = useRef(paused);
	pausedRef.current = paused;

	useEffect(() => {
		if (reducedMotion()) return undefined;
		const timers: number[] = [];
		const later = (fn: () => void, ms: number) => {
			timers.push(window.setTimeout(() => (pausedRef.current ? later(fn, 600) : fn()), ms));
		};
		let k = 0;
		const dive = () => {
			setStep(k);
			setStop(TOUR_STOPS[k]);
			later(() => setCaptionOn(true), 1200);
			later(() => {
				setCaptionOn(false);
				setStop(null);
				k = (k + 1) % TOUR_STOPS.length;
				later(dive, 2700);
			}, 5200);
		};
		later(dive, 2200);
		return () => timers.forEach((t) => window.clearTimeout(t));
	}, []);

	const slugs = useMemo(() => trip.stops.map(([s]) => s), [trip]);
	const fit = useMemo(() => slugs.map(hostelPoint), [slugs]);
	const hostels = useMemo(
		() =>
			slugs.map((s) =>
				hostelMarker(s, { inRoute: true, plate: s === stop, force: s === stop, name: s === stop ? `Mad Monkey ${HOSTELS[s].name}` : HOSTELS[s].name })
			),
		[slugs, stop]
	);
	const pois = useMemo(() => (stop ? poiMarkers(stop) : []), [stop]);
	const focus = useMemo(() => (stop ? { lon: HOSTELS[stop].lon, lat: HOSTELS[stop].lat, zoom: DIVE_ZOOM } : null), [stop]);

	const idx = trip.stops.findIndex(([s]) => s === TOUR_STOPS[step]);
	const nights = trip.stops[idx][1];
	return (
		<div className={styles.hm}>
			<div className={styles.hmMap}>
				<LiveMap
					still
					id="mm-hero"
					label={`Route map: ${trip.name}`}
					hostels={hostels}
					pois={pois}
					route={slugs}
					fit={fit}
					focus={focus}
					interactive={false}
					dots={false}
				/>
			</div>
			<div className={cx(styles.hmCap, captionOn && styles.on)} aria-live="polite">
				<span className={styles.hmN}>{idx + 1}</span>
				<span>
					<small>
						Stop {idx + 1} of {trip.stops.length} · {nights} night{nights > 1 ? "s" : ""}
					</small>
					<b>Mad Monkey {HOSTELS[TOUR_STOPS[step]].name}</b>
				</span>
			</div>
			<div className={styles.hmProg}>
				{TOUR_STOPS.map((s, i) => (
					<i key={s} className={cx(stop && i === step && styles.on)} />
				))}
			</div>
		</div>
	);
};

const Hero = () => {
	const flagship = getTrip(FLAGSHIP_ID)!;
	const [slide, setSlide] = useState(0);
	const [paused, setPaused] = useState(false);

	useEffect(() => {
		if (reducedMotion()) return undefined;
		const timer = window.setInterval(() => setSlide((k) => (k + 1) % SLIDES.length), 4200);
		return () => window.clearInterval(timer);
	}, []);

	return (
		<section className={styles.hero} aria-labelledby="mm-h1">
			<div className={styles.heroS} aria-hidden="true">
				<div className={styles.heroSv}>
					{SLIDES.map((p, i) => (
						<img key={p} className={i === slide ? styles.on : undefined} src={asset(`photos/${p}.jpg`)} alt="" />
					))}
				</div>
			</div>
			<div className={cx(shared.wrap, styles.heroGrid)}>
				<div className={styles.heroCopy}>
					<span className={styles.badge}>
						<b>Partner</b>Stippl × Mad Monkey Hostels
					</span>
					<h1 id="mm-h1">
						Backpack Southeast Asia, <span className={styles.u}>Mad Monkey</span> to Mad Monkey
					</h1>
					<p className={styles.lede}>
						23 hostels in six countries, and a free planner to string them together. Start from a ready-made route or build your own, hostel to hostel, and take
						it with you in Stippl.
					</p>
					<div className={styles.heroCtas}>
						<Link id="mm-hero-build" className={cx(shared.btn, shared.btnMm)} to={{ pathname: "/mad-monkey", hash: "#hostels" }}>
							Build your route <Arrow />
						</Link>
						<Link id="mm-hero-routes" className={cx(shared.btn, shared.btnGhost)} to={{ pathname: "/mad-monkey", hash: "#trips" }}>
							See ready-made routes
						</Link>
					</div>
					<div className={styles.proof}>
						<span>
							<b>23</b>hostels, 6 countries
						</span>
						<span>
							<b>5</b>ready-made routes
						</span>
						<span>
							<b>Daily</b>The Meet Up at every stop
						</span>
					</div>
				</div>
				<div className={styles.heroCard} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
					<HeroMap trip={flagship} paused={paused} />
					<div className={styles.hcBody}>
						<div className={styles.hcTop}>
							<b>
								<a className={styles.stretch} href={routePreviewHref(flagship)}>
									{flagship.name}
								</a>
							</b>
							<span className={styles.saved}>● In Stippl</span>
						</div>
						<span className={styles.hcMeta}>
							{nightsOf(flagship)} nights · {flagship.stops.length} Mad Monkeys ·{" "}
							{countriesOf(flagship.stops.map(([s]) => s)).map((c) => (
								<Flag key={c} cc={c} />
							))}
						</span>
						<div className={styles.hcStops}>
							{flagship.stops.map(([s]) => (
								<span key={s}>{HOSTELS[s].name}</span>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Hero;
